import React, { useState } from 'react';
import { X, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const OpenApiViewer = ({ image, onClose }) => {
  const [showRaw, setShowRaw] = useState(false);
  const schema = image.openapi_schema;

  if (!schema) return null;

  // Extract input schema from Cog's OpenAPI structure
  const inputSchema = schema?.components?.schemas?.Input?.properties || {};
  const requiredFields = schema?.components?.schemas?.Input?.required || [];

  // Extract output schema
  const outputSchema = schema?.components?.schemas?.Output || {};

  // Get description from prediction endpoint or schema info
  const modelTitle = schema?.info?.title || image.name;
  const modelVersion = schema?.info?.version;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center rounded-t-lg">
          <div className="flex items-center space-x-3 text-white">
            <FileText size={20} />
            <div>
              <h2 className="text-lg font-bold">API Specification</h2>
              <p className="text-blue-200 text-xs">{modelTitle} {modelVersion ? `v${modelVersion}` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Input Fields */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Input Parameters</h3>
            {Object.keys(inputSchema).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(inputSchema).map(([fieldName, field]) => {
                  const isRequired = requiredFields.includes(fieldName);
                  const fieldType = field.type || (field.allOf ? 'object' : field.format || 'any');
                  const isFile = fieldType === 'string' && field.format === 'uri';

                  return (
                    <div key={fieldName} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <code className="text-sm font-semibold text-gray-900">{fieldName}</code>
                          {isRequired && (
                            <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">required</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 font-mono">
                          {isFile ? 'file' : fieldType}
                          {field.enum ? ` (${field.enum.join(' | ')})` : ''}
                        </span>
                      </div>
                      {field.description && (
                        <p className="text-xs text-gray-600 mb-1">{field.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 text-[10px]">
                        {field.default !== undefined && (
                          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            default: {JSON.stringify(field.default)}
                          </span>
                        )}
                        {field.minimum !== undefined && (
                          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            min: {field.minimum}
                          </span>
                        )}
                        {field.maximum !== undefined && (
                          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            max: {field.maximum}
                          </span>
                        )}
                        {field.ge !== undefined && (
                          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            &gt;= {field.ge}
                          </span>
                        )}
                        {field.le !== undefined && (
                          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            &lt;= {field.le}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No input parameters defined.</p>
            )}
          </div>

          {/* Output */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wider">Output</h3>
            <div className="bg-gray-50 rounded-lg p-3">
              <code className="text-sm text-gray-700">
                {outputSchema.type || 'any'}
                {outputSchema.format ? ` (${outputSchema.format})` : ''}
              </code>
              {outputSchema.description && (
                <p className="text-xs text-gray-600 mt-1">{outputSchema.description}</p>
              )}
            </div>
          </div>

          {/* Raw JSON toggle */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowRaw(!showRaw)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <span>Raw OpenAPI JSON</span>
              {showRaw ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showRaw && (
              <div className="px-3 pb-3 border-t border-gray-100 pt-3">
                <pre className="text-xs text-gray-700 bg-gray-50 p-3 rounded overflow-x-auto max-h-64 overflow-y-auto">
                  {JSON.stringify(schema, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpenApiViewer;
